import { Helmet } from 'react-helmet-async';
import { baseMetadata, defaultKeywords } from '../../config/metadata';

interface SEOProps {
  title: string;
  description: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
  keywords?: string[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

export default function SEO({
  title,
  description,
  path,
  type = 'website',
  image = '/og-image.jpg',
  keywords = defaultKeywords,
  article,
}: SEOProps) {
  const siteUrl = 'https://aedra.co';
  const fullUrl = `${siteUrl}${path}`;
  const fullTitle = path === '/' ? title : `${title} | Aedra`;
  const keywordContent = keywords.join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywordContent && <meta name="keywords" content={keywordContent} />}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:site_name" content="Aedra" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />
      <meta name="twitter:site" content="@AedraHQ" />

      {/* Article specific meta tags */}
      {type === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Aedra',
          url: siteUrl,
          logo: `${siteUrl}/logo.svg`,
          description:
            'Private-market deal intelligence: custom, source-verified datasets on private equity, venture capital, and M&A activity.',
          foundingDate: String(baseMetadata.foundingYear),
          founder: {
            '@type': 'Person',
            name: baseMetadata.founder.name,
            url: baseMetadata.founder.url,
          },
          sameAs: ['https://x.com/AedraHQ', 'https://linkedin.com/company/aedraco/'],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'sales',
            url: `${siteUrl}/request`,
          },
        })}
      </script>

      {/* Structured Data - WebSite */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Aedra',
          url: siteUrl,
          description:
            'Aedra builds custom, source-verified datasets on private equity, venture capital, and M&A activity.',
        })}
      </script>

      {/* Structured Data - ProfessionalService */}
      {path === '/' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            name: 'Aedra Private-Market Deal Intelligence',
            url: siteUrl,
            description,
            areaServed: 'Worldwide',
            serviceType: [
              'Private equity transaction research',
              'Venture capital financing datasets',
              'M&A comps and deal intelligence',
            ],
          })}
        </script>
      )}

      {/* Structured Data - WebPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: fullTitle,
          description: description,
          url: fullUrl,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Aedra',
            url: siteUrl,
          },
        })}
      </script>

      {/* Structured Data - Article (if applicable) */}
      {type === 'article' && article && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: title,
            description: description,
            image: `${siteUrl}${image}`,
            datePublished: article.publishedTime,
            dateModified: article.modifiedTime || article.publishedTime,
            author: {
              '@type': 'Organization',
              name: article.author || 'Aedra',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Aedra',
              logo: {
                '@type': 'ImageObject',
                url: `${siteUrl}/logo.svg`,
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': fullUrl,
            },
          })}
        </script>
      )}
    </Helmet>
  );
}
